// component-test-check-ignore
import type { FC } from 'react';

import { LocalDate } from '@js-joda/core';
import type { BoxProps } from '@opendoor/bricks-next';
import { Box, Col, Row, Text } from '@opendoor/bricks-next';

import { Link } from 'platform/bricks-next';
import type { LinkProps } from 'platform/bricks-next';
import { LayoutContent } from 'platform/components/layout/Layout';

import { List } from 'shared/components/List';
import { ListItem } from 'shared/components/ListItem';
import {
  PRIVACY_CENTER_URL,
  PRIVACY_POLICY_URL,
  TERMS_OF_SERVICE_URL,
} from 'shared/helpers/routes';
import { EqualHousingOpportunityIcon } from 'shared/helpers/svgCollection';

const Separator: FC = () => (
  <Box
    display="block"
    height="$12x"
    width="$1x"
    backgroundColor="$backgroundInversePrimary"
    mx="$2x"
  />
);

const Trademark: FC = () => {
  return (
    <Row alignItems="center">
      <Text
        tag="p"
        typography="$bodySmall"
        $platform-web={{ fontSize: 12 }}
        color="$contentTertiary"
        data-testid="trademark"
      >
        © {LocalDate.now().year()} Opendoor
      </Text>
      <Separator />
      <Text
        tag="p"
        typography="$bodySmall"
        $platform-web={{ fontSize: 12 }}
        color="$contentTertiary"
      >
        All Rights Reserved
      </Text>
    </Row>
  );
};

type FooterLinkProps = Omit<LinkProps, 'aria-label'> & {
  displayName: string;
};

const FooterLink: FC<FooterLinkProps> = ({ analyticsName, displayName, href, ...props }) => (
  <Link
    analyticsName={analyticsName}
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    typography="$bodyXsmall"
    variant="tertiary"
    {...props}
  >
    {displayName}
  </Link>
);

const LegalLinks: FC<FooterProps> = ({ closingOrClosedOffer = false }) => {
  return (
    <List display="flex" flexWrap="wrap" alignItems="center">
      <ListItem display="flex" flexDirection="row" alignItems="center">
        <FooterLink
          analyticsName="terms-of-service"
          displayName="Terms of Service"
          href={TERMS_OF_SERVICE_URL}
        />
      </ListItem>
      <ListItem display="flex" flexDirection="row" alignItems="center">
        <Separator />
      </ListItem>
      {closingOrClosedOffer && (
        <>
          <ListItem display="flex" flexDirection="row" alignItems="center">
            <FooterLink
              analyticsName="notice-at-collection"
              displayName="Notice at Collection"
              href={`${TERMS_OF_SERVICE_URL}/#ccpa`}
            />
          </ListItem>
          <ListItem display="flex" flexDirection="row" alignItems="center">
            <Separator />
          </ListItem>
        </>
      )}
      <ListItem display="flex" flexDirection="row" alignItems="center">
        <FooterLink
          analyticsName="privacy-policy"
          displayName="Privacy Policy"
          href={PRIVACY_POLICY_URL}
        />
      </ListItem>
      <ListItem display="flex" flexDirection="row" alignItems="center">
        <Separator />
      </ListItem>
      <ListItem display="flex" flexDirection="row" alignItems="center">
        <FooterLink
          analyticsName="do-not-sell-my-info"
          displayName="Do Not Sell My Info"
          href={PRIVACY_CENTER_URL}
        />
      </ListItem>
    </List>
  );
};

type FooterProps = {
  closingOrClosedOffer?: boolean;
};

/**
 * The footer that appears on all pages.
 */
export const Footer: FC<FooterProps & BoxProps> = ({ closingOrClosedOffer, ...props }) => (
  <LayoutContent
    flexDirection="row"
    $smallerThanMD={{ flexWrap: 'wrap' }}
    data-testid="global-footer"
    justifyContent="space-between"
    gap="$8x"
    {...props}
  >
    <Col>
      <Trademark />
      <LegalLinks closingOrClosedOffer={closingOrClosedOffer} />
    </Col>
    <Row alignItems="center">
      <EqualHousingOpportunityIcon color="$contentTertiary" />
    </Row>
  </LayoutContent>
);
